import { t } from 'i18next';
import { camelCase, upperFirst } from 'lodash';

import { IElement } from 'bom/types';
import { IManagedNote } from 'core/components';
import { IComponentNote } from 'jobs/types';
import { Color } from 'theme/enums';

import { JobPartNoteType } from './enums';

export const getMappedNotesArrByType = (
  notes: (IComponentNote & { tags?: string[] })[],
  type: JobPartNoteType,
  editable = true
): IManagedNote[] =>
  mapNotesToManagedNotes(
    notes.map((note) => ({
      ...note,
      type,
    })),
    editable
  ).reverse();

export const mapJobPartNotesToManagedNotes = (
  jobPartElements: IElement[] = [],
  componentNotes: IComponentNote[] = [],
  elementNotesMap: Record<number, IComponentNote[]> = {},
  element?: IElement
) => {
  const mappedComponentNotes = getMappedNotesArrByType(componentNotes, JobPartNoteType.DEFAULT);

  const mappedElementNotes = mapElementNotesMapToManagedNotes(
    jobPartElements,
    elementNotesMap,
    element
  );

  return [...mappedComponentNotes, ...mappedElementNotes];
};

export const mapElementNotesMapToManagedNotes = (
  jobPartElements: IElement[] = [],
  elementNotesMap: Record<number, IComponentNote[]>,
  element?: IElement
) => {
  const elementNotes = [];

  if (element) {
    if (!elementNotesMap?.[element.id]) {
      return [];
    }

    elementNotes.push(
      ...elementNotesMap[element.id].map((note: IComponentNote) => ({
        ...note,
        tags: [element.name],
      }))
    );
  } else {
    const elementNamesMap =
      jobPartElements?.reduce(
        (acc: Record<number, string>, element: IElement) => {
          acc[element.id] = element.name;

          return acc;
        },
        {} as Record<number, string>
      ) || {};

    elementNotes.push(
      ...(Object.entries(elementNotesMap).flatMap(([elementId, notes]) => {
        const elementName = elementNamesMap[Number(elementId)];

        return (notes as IComponentNote[]).map((note: IComponentNote) => ({
          ...note,
          tags: [elementName],
        }));
      }) as IComponentNote[])
    );
  }

  return getMappedNotesArrByType(elementNotes, JobPartNoteType.ELEMENT, !!element?.id);
};

const getNoteLabel = (type: JobPartNoteType, withNoteSuffix = true) =>
  t(`labelCreate${upperFirst(camelCase(type))}${withNoteSuffix ? 'Note' : ''}`);

const getNoteCategoryColor = (type: JobPartNoteType) =>
  [JobPartNoteType.DEFAULT].includes(type) ? Color.PRIMARY : Color.SUCCESS;

const getNoteSx = (type: JobPartNoteType) =>
  [JobPartNoteType.DEFAULT].includes(type)
    ? { backgroundColor: 'primary.50' }
    : { backgroundColor: 'success.50' };

const mapNotesToManagedNotes = (
  notes: (IComponentNote & { type: JobPartNoteType })[] = [],
  editable = true
) =>
  notes.map((note) => ({
    ...note,
    isEditable: editable,
    isDeletable: editable,
    category: {
      label: getNoteLabel(note.type),
      color: getNoteCategoryColor(note.type),
    },
    sx: getNoteSx(note.type),
  }));
